import Flex from "components/Flex";
import { CSSProperties, FC, MutableRefObject, useCallback, useContext, useState } from "react";
import { P } from "theme/typography";
import theme, { ClickableIconSX, EllipsisSX, HoverLinkSX, SimpleHoverLinkSX } from "theme";
import { Button, FormControl, InputLabel, MenuItem, Select, Tooltip, Box, IconButton } from "@mui/material";
import { GetApp, MenuOpen, OpenInNew } from "@mui/icons-material";
import { useAppSelector } from "redux/hooks";
import { selectFormFields, selectFormPagesVisible, selectFormSections } from "redux/slices/form.slice";
import ExportModal from "components/ExportModal";
import CollapsibleSections, { CollapsibleSection } from "components/CollapsibleSections";
import { CaseInfo, OutcomeStatus } from "generated/graphql";
import { Link } from "react-router-dom";
import { selectCase, selectCaseInsightsPending, selectSectionValues } from "redux/slices/case";
import { SectionValidationIcon, SectionValueIcon } from "./SectionOutcomes";
import CaseOutcomeStatus from "./CaseOutcomeStatus";
import { displayOutcomeStatus, displayValidationStatus, FormFieldValuesUI, parseSafeInt } from "Backend";
import {
  selectCaseInsightsEnabled,
  selectLensOutcomeStatusConfiguration,
  selectLookerSupplementalDataReport,
} from "redux/slices/clientLens";
import { isFormValueAnswered } from "helpers/isFormValueAnswered";
import { CaseFormContext } from "contexts/CaseFormContext";
import { AppRoutes } from "AppRoutes";
import { NewGreys } from "../../../../theme/palette";
import { flushSync } from "react-dom";
import { useRunAutoScripts } from "helpers/hooks/useRunAutoScripts";
import { CaseInsightsRefreshTooltip } from "./CaseInsightsRefreshTooltip";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import { stripNull } from "helpers/coll";

interface CaseOutlineProps {
  formValues: FormFieldValuesUI[];
  fieldContainerRef: MutableRefObject<HTMLElement | null>;
  caseId: number;
  caseInfo: CaseInfo | undefined;
  activeSectionId: number | undefined;
}

const CaseOutline: FC<CaseOutlineProps> = ({ formValues, fieldContainerRef, caseId, caseInfo, activeSectionId }) => {
  const formSections = useAppSelector(selectFormSections);
  const formPages = useAppSelector(selectFormPagesVisible);
  const sectionValues = useAppSelector(selectSectionValues);
  const caseInsightsPending = useAppSelector(selectCaseInsightsPending);
  const { sbrEnabled, lensOutcomeStatusConfigurations } = useAppSelector(selectLensOutcomeStatusConfiguration);
  const { searchParams, updateParams } = useSearchParamsWithUpdate();
  const paramsFormPageId = parseSafeInt(searchParams.get("formPageId"));
  const firstFormPage = formPages[0];
  const formPageId =
    paramsFormPageId !== undefined ? paramsFormPageId : firstFormPage !== undefined ? firstFormPage.id : undefined;
  const lensCase = useAppSelector(selectCase);
  const formFields = useAppSelector(selectFormFields);
  const caseForm = useContext(CaseFormContext);
  const lookerSupplementalDataReport = useAppSelector(selectLookerSupplementalDataReport);
  const [open, setOpen] = caseForm.outlineOpenState;
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);
  const caseInsightsEnabled = useAppSelector(selectCaseInsightsEnabled);

  const runAutoScripts = useRunAutoScripts(caseId);

  const scrollFieldIntoView = useCallback(
    (fieldId: number) => {
      if (fieldContainerRef.current) {
        const node = fieldContainerRef.current.querySelector(`#form-field-${fieldId}`);
        if (node) {
          node.scrollIntoView();
        }
      }
    },
    [fieldContainerRef.current]
  );

  const exportBody = (
    <Flex>
      <P variant="h6">Profile Export Settings</P>
      {caseInfo && (
        <P>
          {caseInfo.claimantGivenName} {caseInfo.claimantSurname}
        </P>
      )}
    </Flex>
  );

  const fields = (sectionId?: number) => {
    return formFields
      .filter((f) => (sectionId !== undefined ? f.sectionId === sectionId : true))
      .map((field) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginY: sectionId === undefined ? 1 : undefined,
          }}
          key={field.id}>
          <Box sx={EllipsisSX}>
            <P
              variant="body4"
              onClick={() => {
                flushSync(() => {
                  updateParams([
                    { key: "formPageId", value: formPageId?.toString() },
                    { key: "sectionId", value: stripNull(field.sectionId)?.toString() },
                  ]);
                });
                scrollFieldIntoView(field.id);
              }}
              sx={HoverLinkSX}
              id={"form_outline_" + field.id}>
              {open ? (
                <>
                  <Box sx={{ display: "inline-block", textAlign: "right", mr: 1 }}>{field.outlinePrefix || ""}</Box>
                  {field.name}
                  {field.required && "*"}
                </>
              ) : (
                <Tooltip title={`${field.outlinePrefix} ${field.name}`}>
                  <>{field.outlinePrefix || ""}</>
                </Tooltip>
              )}
            </P>
          </Box>
          {isFormValueAnswered(
            field,
            formValues.find((f) => f.fieldId === field.id)
          ) && (
            <Box sx={{ flexShrink: 0, width: 18, display: "flex", justifyContent: "center" }}>
              <Box sx={{ borderRadius: "50%", height: 7, width: 7, backgroundColor: "grey.100" }} />
            </Box>
          )}
        </Box>
      ));
  };

  const sections: CollapsibleSection[] = formSections
    .filter((s) => s.formPageId === formPageId)
    .map((section, index) => {
      const sectionValue = sectionValues.find((sv) => sv.sectionId === section.id);
      const sectionDisabled = sectionValue?.outcomeStatus === OutcomeStatus.NotApplicable;
      return {
        title: (
          <Box sx={{ flexShrink: 1, ...EllipsisSX, my: 1, display: "flex", flexGrow: 1, pr: 1, alignItems: "center" }}>
            <Link
              to={`?formPageId=${formPageId}&sectionId=${section.id}`}
              title={section.name}
              style={EllipsisSX as CSSProperties}>
              <P
                variant="smSemiBold"
                sx={{
                  ...SimpleHoverLinkSX,
                  color: sectionDisabled ? theme.palette.grey[400] : theme.palette.grey[600],
                }}>
                {open ? (
                  section.name
                ) : (
                  <Tooltip title={section.name}>
                    <>{index + 1}</>
                  </Tooltip>
                )}
              </P>
            </Link>
            <Box sx={{ flexGrow: 1 }} />
            {!sectionDisabled && section.outcomeEnabled && open && (
              <Tooltip
                title={displayOutcomeStatus(sectionValue?.outcomeStatus, sbrEnabled, lensOutcomeStatusConfigurations)}
                arrow>
                <IconButton
                  onClick={(evt) => {
                    evt.stopPropagation();
                    updateParams([
                      { key: "sectionId", value: section.id.toString() },
                      { key: "section", value: "review" },
                    ]);
                  }}>
                  <SectionValueIcon
                    outcomeStatus={sectionValue?.outcomeStatus}
                    sx={{ borderColor: theme.palette.grey[900] }}
                  />
                </IconButton>
              </Tooltip>
            )}
            {!sectionDisabled && section.validationEnabled && open && (
              <Tooltip title={displayValidationStatus(sectionValue?.validationStatus)} arrow>
                <IconButton
                  onClick={(evt) => {
                    evt.stopPropagation();
                    updateParams([
                      { key: "sectionId", value: section.id.toString() },
                      { key: "section", value: "qa" },
                    ]);
                  }}>
                  <SectionValidationIcon
                    status={sectionValue?.validationStatus}
                    sx={{ borderColor: theme.palette.grey[900] }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
        items: sectionDisabled ? [] : fields(section.id),
        expanded: true,
        selected: activeSectionId === section.id,
        disabled: sectionDisabled,
      };
    });

  return (
    <Flex
      sx={{
        height: "100%",
        minWidth: 0,
        width: open ? 320 : 72,
        backgroundColor: NewGreys.background1,
        px: open ? 2 : 1,
        pt: 0.7,
      }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        {open && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {lensCase.outcomeStatus !== null && lensCase.outcomeStatus !== undefined && (
              <Box sx={{ mt: 1, alignSelf: "flex-start" }}>
                <CaseOutcomeStatus status={lensCase.outcomeStatus} />
              </Box>
            )}
            {lookerSupplementalDataReport && (
              <Button
                variant="text"
                size="small"
                sx={{ mt: 0.5, px: 0 }}
                endIcon={<OpenInNew />}
                href={AppRoutes.supplementalDataURLBuilder(
                  lensCase.lensId,
                  lensCase.id,
                  lookerSupplementalDataReport.id
                )}
                target="_blank"
                rel="noopener noreferrer">
                Supplemental Data
              </Button>
            )}
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "center", width: !open ? "100%" : undefined, mt: 1 }}>
          <Tooltip title={open ? "Close Overview" : "Open Overview"} arrow>
            <MenuOpen
              sx={{ ...ClickableIconSX, transform: !open ? "rotate(180deg)" : "" }}
              onClick={() => setOpen((cur) => !cur)}
              data-pendo="case-toggle-overview"
            />
          </Tooltip>
        </Box>
      </Box>
      {open && formPages.length > 1 && (
        <FormControl size="small" variant="standard" fullWidth sx={{ mt: 2 }}>
          <InputLabel>Page</InputLabel>
          <Select
            value={formPageId}
            label="Page"
            onChange={(evt) => {
              updateParams([
                { key: "formPageId", value: evt.target.value.toString() },
                { key: "sectionId", value: undefined },
                { key: "fieldId", value: undefined },
              ]);
            }}>
            {formPages.map((fp) => (
              <MenuItem value={fp.id} key={fp.id}>
                {fp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Box
        sx={{ overflowY: "auto", overflowX: "hidden", py: 1, pt: 2, "> *": { marginY: sections.length > 1 ? 0 : 1 } }}>
        <CollapsibleSections sections={sections} />
      </Box>
      {open && (
        <Box sx={{ py: 2 }}>
          <Button
            id="case_form_export"
            size="large"
            variant="outlined"
            color="default"
            sx={{ width: "100%" }}
            onClick={() => setExportModalOpen(true)}
            startIcon={<GetApp />}>
            Export
          </Button>
          {caseInsightsEnabled && (
            <CaseInsightsRefreshTooltip>
              <Button
                size="large"
                variant="outlined"
                color="default"
                sx={{ width: "100%", mt: 2 }}
                onClick={runAutoScripts}
                disabled={caseInsightsPending}>
                {caseInsightsPending ? "Refreshing Insights..." : "Refresh Case Insights"}
              </Button>
            </CaseInsightsRefreshTooltip>
          )}
        </Box>
      )}

      {exportModalOpen && (
        <ExportModal body={exportBody} open={exportModalOpen} setOpen={setExportModalOpen} caseId={caseId} />
      )}
    </Flex>
  );
};

export default CaseOutline;
